'use client'

import { useState } from 'react'
import { CheckCircle } from 'lucide-react'
import { ApplyModal } from './apply-modal'
import type { ApplicationStatus } from '@/types/api.types'

interface ApplyCtaProps {
  jobId: string
  jobTitle: string
  companyName: string
  hasApplied: boolean
  applicationStatus: ApplicationStatus | null
  isClosed: boolean
}

const STATUS_LABELS: Record<string, string> = {
  new: 'You applied to this job',
  reviewed: 'Your application is being reviewed',
  shortlisted: 'You have been shortlisted',
  assessment: 'Assessment in progress',
  interview: 'Interview stage',
  offered: 'Offer extended',
  hired: 'Hired',
  rejected: 'Not selected',
  withdrawn: 'Withdrawn',
  closed: 'Position closed',
}

export function ApplyCta({ jobId, jobTitle, companyName, hasApplied, applicationStatus, isClosed }: ApplyCtaProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const [showModal, setShowModal] = useState(false)
  const [applied, setApplied] = useState(hasApplied)

  if (isClosed) return null

  if (applied) {
    const label = applicationStatus ? (STATUS_LABELS[applicationStatus] ?? 'You applied to this job') : 'You applied to this job'
    return (
      <div className="flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-success)]/30 bg-[var(--color-success)]/5 px-4 py-3">
        <CheckCircle className="h-4 w-4 text-[var(--color-success)]" />
        <span className="text-sm font-medium text-[var(--color-text-primary)]" style={font}>
          {label}
        </span>
      </div>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="w-full rounded-[var(--radius-md)] bg-gradient-to-r from-[var(--color-brand-indigo)] to-[var(--color-brand-violet)] px-6 py-3 text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98]"
        style={font}
      >
        Apply Now
      </button>

      {showModal && (
        <ApplyModal
          jobId={jobId}
          jobTitle={jobTitle}
          companyName={companyName}
          onClose={() => setShowModal(false)}
          onSuccess={() => setApplied(true)}
        />
      )}
    </>
  )
}
